"use client";

import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

// Renders an article's markdown body with site typography. External links open
// in a new tab; internal links stay in-tab. SSR'd to static HTML for SEO.
const components: Components = {
  // Tables are wide; let them scroll rather than blowing out the page.
  table({ children }) {
    return (
      <div className="my-6 overflow-x-auto">
        <table className="w-full border-collapse text-sm md:text-base">{children}</table>
      </div>
    );
  },
  a({ href, children }) {
    const isExternal = /^https?:\/\//.test(href ?? "");
    return isExternal ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[var(--primary)] underline underline-offset-2 hover:no-underline"
      >
        {children}
      </a>
    ) : (
      <a
        href={href}
        className="text-[var(--primary)] underline underline-offset-2 hover:no-underline"
      >
        {children}
      </a>
    );
  },
};

export function ArticleBody({ content }: { content: string }) {
  return (
    <div
      className="mt-8 font-polysans text-[var(--foreground)]/90 leading-relaxed text-base md:text-lg
        [&_p]:my-5 [&_p:first-child]:mt-0
        [&_h2]:font-script [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:text-[var(--foreground)] [&_h2]:mt-10 [&_h2]:mb-3
        [&_h3]:font-polysans [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[var(--foreground)] [&_h3]:mt-8 [&_h3]:mb-2
        [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-2
        [&_strong]:text-[var(--foreground)] [&_strong]:font-semibold
        [&_img]:my-8 [&_img]:w-full [&_img]:rounded-xl [&_img]:border [&_img]:border-[var(--border)]
        [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--border)] [&_blockquote]:pl-4 [&_blockquote]:italic
        [&_th]:border-b [&_th]:border-[var(--border)] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[var(--foreground)] [&_th]:whitespace-nowrap
        [&_td]:border-b [&_td]:border-[var(--border)]/50 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
